import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LayoutGrid, ChevronDown } from 'lucide-react';
import { buildProductsPath } from '../lib/seoUrls';
import { useDynamicTaxonomyTranslation } from '../hooks/useDynamicTaxonomyTranslation';
import { useCategoriesData } from '../hooks/useCategoriesData';

/**
 * Mobile-friendly accordion variant of CategoriesMegaMenu.
 * Renders an inline collapsible list (no absolute popups) so it works
 * inside the hamburger menu on small screens.
 */
export const MobileCategoriesAccordion = ({ onNavigate }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [open, setOpen] = useState(false);
  const [openCatId, setOpenCatId] = useState(null);
  const { categories } = useCategoriesData();

  useDynamicTaxonomyTranslation(categories);

  const close = () => { if (onNavigate) onNavigate(); };

  if (categories.length === 0) return null;

  return (
    <div data-testid="mobile-categories-accordion">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-[#1C241E] hover:text-[#D96C4A]"
        data-testid="mobile-categories-trigger"
        aria-expanded={open}
      >
        <span className="inline-flex items-center gap-2">
          <LayoutGrid className="w-4 h-4" />
          {t('nav.categories', 'Categories')}
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="mt-2 ml-2 border-l border-[rgba(28,36,30,0.12)] pl-3 max-h-[50vh] overflow-y-auto space-y-1" data-testid="mobile-categories-list">
          {categories.map((cat) => {
            const isOpen = openCatId === cat.id;
            const subs = cat.subcategories || [];
            return (
              <div key={cat.id} className="text-sm">
                <div className="flex items-center justify-between">
                  <Link
                    to={buildProductsPath(lang, cat.id)}
                    onClick={close}
                    className="flex-1 truncate text-[#1C241E] hover:text-[#D96C4A] py-1"
                    data-testid={`mobile-menu-cat-${cat.id}`}
                  >
                    {t(`cat.${cat.id}`, cat.name)}
                  </Link>
                  {subs.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setOpenCatId(isOpen ? null : cat.id)}
                      className="p-1 text-[#5C6B61] hover:text-[#D96C4A]"
                      aria-expanded={isOpen}
                      aria-label="Toggle subcategories"
                      data-testid={`mobile-menu-cat-toggle-${cat.id}`}
                    >
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                  )}
                </div>
                {isOpen && subs.length > 0 && (
                  <ul className="ml-3 pl-2 border-l border-[rgba(28,36,30,0.08)] space-y-0.5 pb-1">
                    {subs.map((sub) => (
                      <li key={sub.id}>
                        <Link
                          to={buildProductsPath(lang, cat.id, sub.id)}
                          onClick={close}
                          className="block text-xs text-[#5C6B61] hover:text-[#D96C4A] py-0.5 truncate"
                          data-testid={`mobile-menu-sub-${sub.id}`}
                        >
                          {t(`sub.${sub.id}`, sub.name)}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MobileCategoriesAccordion;
